import React, { useState, useEffect } from 'react';
import { Trash2, Edit, X, Users } from 'lucide-react';
import { Alert } from '../../ui/alert';
import { useSubscriptionLimits } from "../../../hooks/useSubscriptionLimits";
import { SubscriptionLimitModal } from "../../../components/subscription/SubscriptionLimitModal";
import { useTranslation } from 'react-i18next';

const CreateTeamModal = ({ onClose, onCreate, editTeam = null }) => {
  const [teamName, setTeamName] = useState('');
  const [employees, setEmployees] = useState([]);
  const [currentEmployee, setCurrentEmployee] = useState({
    name: '',
    wkl_wrk_load: '',
    day_WI: '',
    number: ''
  });
  const [editingIndex, setEditingIndex] = useState(null);
  const [error, setError] = useState('');
  const [showLimitModal, setShowLimitModal] = useState(false);
  const { limits } = useSubscriptionLimits();
  const { t } = useTranslation();

  useEffect(() => {
      if (editTeam) {
          setTeamName(editTeam.name);
          setEmployees(editTeam.employees.map(emp => ({
              name: emp.name,
              wkl_wrk_load: emp.wkl_Wrk_Load / 10,
              day_WI: emp.day_WI / 10,
              number: emp.number
          })));
      }
  }, [editTeam]);

  const isEmployeeNumberDuplicate = (number, currentIndex = null) => {
    return employees.some((emp, index) => 
      emp.number === number && index !== currentIndex
    );
  };

  const handleAddEmployee = () => {
    if (!limits?.isProUser && employees.length >= limits?.maxEmployees) {
      setShowLimitModal(true);
      return;
    }

    if (!currentEmployee.name || !currentEmployee.wkl_wrk_load || 
        !currentEmployee.day_WI || !currentEmployee.number) {
      setError(t("teams.create.error.fillAllFields"));
      return;
    }

    if (isEmployeeNumberDuplicate(currentEmployee.number, editingIndex)) {
      setError(t("teams.create.error.duplicateNumber"));
      return;
    }

    if (editingIndex !== null) {
      const updatedEmployees = [...employees];
      updatedEmployees[editingIndex] = { ...currentEmployee };
      setEmployees(updatedEmployees);
      setEditingIndex(null);
    } else {
      setEmployees([...employees, { ...currentEmployee }]);
    }

    setCurrentEmployee({
      name: '',
      wkl_wrk_load: '',
      day_WI: '',
      number: ''
    });
    setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!teamName.trim()) {
      setError(t("teams.create.error.nameRequired"));
      return;
    }
    if (employees.length === 0) {
      setError(t("teams.create.error.atLeastOneEmployee"));
      return;
    }
    onCreate(editTeam?.id, teamName, employees);
  };


    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50 overflow-y-auto">
        <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 sm:p-6 border-b">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="h-8 w-8 sm:h-10 sm:w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                <Users className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600" />
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                {editTeam ? t("teams.edit.title") : t("teams.create.title")}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-500 rounded-full hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 p-4 sm:p-6 overflow-y-auto">
            {error && (
              <Alert variant="destructive" className="mb-4 sm:mb-6">
                {error}
              </Alert>
            )}

            <div className="mb-4 sm:mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("teams.create.name")}
              </label>
              <input
                type="text"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder={t("teams.create.namePlaceholder")}
              />
            </div>

            <div className="bg-gray-50 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">{t("teams.create.employees")}</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 mb-3 sm:mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t("teams.create.employee.name")}</label>
                  <input
                    type="text"
                    value={currentEmployee.name}
                    onChange={(e) => setCurrentEmployee({...currentEmployee, name: e.target.value})}
                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t("teams.create.employee.number")}</label>
                  <input
                    type="number"
                    value={currentEmployee.number}
                    onChange={(e) => setCurrentEmployee({...currentEmployee, number: e.target.value})}
                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t("teams.create.employee.weeklyWorkload")}</label>
                  <input
                    type="number"
                    value={currentEmployee.wkl_wrk_load}
                    onChange={(e) => setCurrentEmployee({...currentEmployee, wkl_wrk_load: e.target.value})}
                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t("teams.create.employee.dailyWorkload")}</label>
                  <input
                    type="number"
                    value={currentEmployee.day_WI}
                    onChange={(e) => setCurrentEmployee({...currentEmployee, day_WI: e.target.value})}
                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={handleAddEmployee}
                className="w-full py-2 sm:py-2.5 text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 font-medium transition-colors"
              >
                {editingIndex !== null ? t("teams.create.updateEmployee") : t("teams.create.addEmployee")}
              </button>
            </div>

            {employees.length > 0 && (
              <div className="relative">
                <h4 className="text-sm font-medium text-gray-700 mb-2 sm:mb-3">{t("teams.members.title")}</h4>
                <div className="border rounded-lg overflow-hidden h-48 sm:h-64">
                  <div className="max-h-full overflow-auto">
                    <div className="block sm:hidden">
                      {employees.map((emp, index) => (
                        <div key={index} className="border-b p-3 last:border-b-0">
                          <div className="flex justify-between items-start mb-2">
                            <div className="font-medium">{emp.name}</div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => {
                                  setCurrentEmployee(emp);
                                  setEditingIndex(index);
                                }}
                                className="p-1 text-gray-400 hover:text-blue-600 rounded-full hover:bg-gray-100"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  const newEmployees = [...employees];
                                  newEmployees.splice(index, 1);
                                  setEmployees(newEmployees);
                                  if (editingIndex === index) {
                                    setEditingIndex(null);
                                    setCurrentEmployee({
                                      name: '',
                                      wkl_wrk_load: '',
                                      day_WI: '',
                                      number: ''
                                    });
                                  }
                                }}
                                className="p-1 text-gray-400 hover:text-red-600 rounded-full hover:bg-gray-100"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-1 text-xs text-gray-500">
                            <div>
                              <span className="block text-gray-400">{t("teams.members.number")}</span>
                              {emp.number}
                            </div>
                            <div>
                              <span className="block text-gray-400">{t("teams.members.weeklyHours")}</span>
                              {emp.wkl_wrk_load}h
                            </div>
                            <div>
                              <span className="block text-gray-400">{t("teams.members.dailyHours")}</span>
                              {emp.day_WI}h
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <table className="hidden sm:table w-full">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t("teams.create.employee.name")}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t("teams.members.number")}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t("teams.members.weeklyHours")}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t("teams.members.dailyHours")}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t("teams.create.actions")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {employees.map((emp, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">{emp.name}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{emp.number}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{emp.wkl_wrk_load}h</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{emp.day_WI}h</td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    setCurrentEmployee(emp);
                                    setEditingIndex(index);
                                  }}
                                  className="p-1 text-gray-400 hover:text-blue-600 rounded-full hover:bg-gray-100"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    const newEmployees = [...employees];
                                    newEmployees.splice(index, 1);
                                    setEmployees(newEmployees);
                                    if (editingIndex === index) {
                                      setEditingIndex(null);
                                      setCurrentEmployee({
                                        name: '',
                                        wkl_wrk_load: '',
                                        day_WI: '',
                                        number: ''
                                      });
                                    }
                                  }}
                                  className="p-1 text-gray-400 hover:text-red-600 rounded-full hover:bg-gray-100"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 border-t bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t("teams.create.cancel")}
            </button>
            <button
              onClick={handleSubmit}
              className="px-3 sm:px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
            >
              {editTeam ? t("teams.edit.save") : t("teams.create.button")}
            </button>
          </div>
        </div>

        <SubscriptionLimitModal 
          isOpen={showLimitModal}
          onClose={() => setShowLimitModal(false)}
          message={`Free plan is limited to ${limits?.maxEmployees} employees per roster. Upgrade to Professional plan for unlimited employees!`}
        />
      </div>
    );
};

export default CreateTeamModal;